import { useAuthor } from '@/hooks/useAuthor';
import {
  useAuthorNotes, useAuthorImages, useAuthorVideos, useAuthorMusic, useAuthorMagazines,
  useAuthorNotesCW, useAuthorImagesCW, useAuthorVideosCW, useAuthorMusicCW, useAuthorMagazinesCW,
} from '@/hooks/useAuthorEvents';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import type { NostrEvent } from '@nostrify/nostrify';
import { Globe, Music, BookOpen, Video, Image as ImageIcon, FileText, User } from 'lucide-react';

const PUBKEY = '6f7c7936efdaec8e51dc3dec3e1b97990151c5a53a077569f4451ad5b48a90b1';

function ProfileCard() {
  const author = useAuthor(PUBKEY);
  const metadata = author.data?.metadata;

  if (author.isLoading) {
    return (
      <Card className="overflow-hidden">
        <div className="h-32 bg-muted animate-pulse" />
        <CardContent className="pt-0 -mt-12 pb-6">
          <Skeleton className="h-20 w-20 rounded-full border-4 border-background mb-4" />
          <Skeleton className="h-6 w-48 mb-2" />
          <Skeleton className="h-4 w-full mb-1" />
          <Skeleton className="h-4 w-3/4" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="overflow-hidden">
      {metadata?.banner && (
        <div className="h-40 overflow-hidden">
          <img src={metadata.banner} alt="Banner" className="w-full h-full object-cover" />
        </div>
      )}
      <CardContent className={`pb-6 ${metadata?.banner ? 'pt-0 -mt-12' : 'pt-6'}`}>
        <Avatar className="h-20 w-20 border-4 border-background mb-4">
          <AvatarImage src={metadata?.picture} />
          <AvatarFallback><User className="h-8 w-8" /></AvatarFallback>
        </Avatar>
        <h3 className="text-xl font-bold">{metadata?.display_name || metadata?.name || 'Hands Behind Your Back!'}</h3>
        {metadata?.nip05 && (
          <p className="text-sm text-muted-foreground mb-2">{metadata.nip05}</p>
        )}
        <p className="text-sm text-foreground/80 whitespace-pre-wrap mt-2 leading-relaxed">{metadata?.about}</p>
        <div className="flex flex-wrap gap-2 mt-4">
          {metadata?.website && (
            <a href={metadata.website} target="_blank" rel="noopener noreferrer"
               className="flex items-center gap-1 text-xs text-primary hover:underline">
              <Globe className="h-3 w-3" /> {metadata.website}
            </a>
          )}
          {metadata?.lud16 && (
            <Badge variant="secondary" className="text-xs">⚡ {metadata.lud16}</Badge>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function NoteCard({ event }: { event: NostrEvent }) {
  const author = useAuthor(event.pubkey);
  const metadata = author.data?.metadata;
  const date = new Date(event.created_at * 1000).toLocaleDateString();

  // Extract image URLs from content
  const imageUrls = event.content.match(/https?:\/\/\S+\.(?:jpg|jpeg|png|gif|webp)/gi) || [];
  const textContent = event.content.replace(/https?:\/\/\S+/gi, '').trim();

  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardContent className="pt-4 pb-4">
        <div className="flex items-start gap-3">
          <Avatar className="h-8 w-8 shrink-0">
            <AvatarImage src={metadata?.picture} />
            <AvatarFallback>{(metadata?.name || 'H')[0]}</AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className="font-medium text-sm">{metadata?.name || 'Hands Behind Your Back!'}</span>
              <span className="text-xs text-muted-foreground">{date}</span>
            </div>
            <p className="text-sm text-foreground/90 break-words whitespace-pre-wrap">{textContent || event.content.substring(0, 300)}</p>
            {imageUrls.length > 0 && (
              <div className="grid grid-cols-2 gap-1 mt-2">
                {imageUrls.slice(0, 4).map((url, i) => (
                  <img key={i} src={url} alt="" className="rounded w-full h-32 object-cover" loading="lazy" />
                ))}
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function ImageCard({ event }: { event: NostrEvent }) {
  // Collect all images from imeta tags (NIP-68 supports multiple images per event)
  const imetaTags = event.tags.filter(t => t[0] === 'imeta');
  const imageUrls: { url: string; alt: string }[] = imetaTags
    .map(tag => {
      const url = tag.find(v => v.startsWith('url '))?.replace('url ', '');
      const alt = tag.find(v => v.startsWith('alt '))?.replace('alt ', '') ?? '';
      return url ? { url, alt } : null;
    })
    .filter((x): x is { url: string; alt: string } => x !== null);

  // Fallback: url tag or URL in content
  if (imageUrls.length === 0) {
    const urlTag = event.tags.find(t => t[0] === 'url')?.[1];
    const contentUrl = event.content.match(/https?:\/\/\S+\.(?:jpg|jpeg|png|gif|webp)/i)?.[0];
    const fallback = urlTag ?? contentUrl;
    if (fallback) imageUrls.push({ url: fallback, alt: '' });
  }

  const title = event.tags.find(t => t[0] === 'title')?.[1] || event.content.substring(0, 60);
  const date = new Date(event.created_at * 1000).toLocaleDateString();

  if (imageUrls.length === 0) return null;

  // Single image: simple square card
  if (imageUrls.length === 1) {
    return (
      <Card className="overflow-hidden hover:shadow-md transition-shadow">
        <img src={imageUrls[0].url} alt={imageUrls[0].alt || title} className="w-full aspect-square object-cover" loading="lazy" />
        <CardContent className="py-2 px-3">
          <p className="text-xs text-muted-foreground">{date}</p>
          {title && <p className="text-sm font-medium line-clamp-2">{title}</p>}
        </CardContent>
      </Card>
    );
  }

  // Multiple images: grid gallery
  const gridCols = imageUrls.length === 2 ? 'grid-cols-2' : imageUrls.length === 3 ? 'grid-cols-2' : 'grid-cols-2';
  return (
    <Card className="overflow-hidden hover:shadow-md transition-shadow col-span-1 sm:col-span-2">
      <div className={`grid ${gridCols} gap-0.5`}>
        {imageUrls.map((img, i) => {
          // For 3 images: first image spans full width on top
          const isFeature = imageUrls.length === 3 && i === 0;
          return (
            <div key={i} className={isFeature ? 'col-span-2' : ''}>
              <img
                src={img.url}
                alt={img.alt || title}
                className={`w-full object-cover ${isFeature ? 'aspect-video' : 'aspect-square'}`}
                loading="lazy"
              />
            </div>
          );
        })}
      </div>
      <CardContent className="py-2 px-3">
        <div className="flex items-center gap-2">
          <p className="text-xs text-muted-foreground">{date}</p>
          <span className="text-xs text-muted-foreground">· {imageUrls.length} images</span>
        </div>
        {title && <p className="text-sm font-medium line-clamp-2">{title}</p>}
      </CardContent>
    </Card>
  );
}

function VideoCard({ event }: { event: NostrEvent }) {
  const imetaTag = event.tags.find(t => t[0] === 'imeta');
  const videoUrl = imetaTag?.find(v => v.startsWith('url '))?.replace('url ', '');
  const thumbnail = imetaTag?.find(v => v.startsWith('image '))?.replace('image ', '')
    || event.tags.find(t => t[0] === 'thumb')?.[1]
    || event.tags.find(t => t[0] === 'image')?.[1];
  const title = event.tags.find(t => t[0] === 'title')?.[1] || event.content.substring(0, 60) || 'Video';
  const date = new Date(event.created_at * 1000).toLocaleDateString();

  return (
    <Card className="overflow-hidden hover:shadow-md transition-shadow">
      {thumbnail ? (
        <div className="relative">
          <img src={thumbnail} alt={title} className="w-full aspect-video object-cover" loading="lazy" />
          <div className="absolute inset-0 flex items-center justify-center bg-black/20">
            <div className="w-12 h-12 rounded-full bg-white/90 flex items-center justify-center">
              <Video className="h-5 w-5 text-gray-800 ml-1" />
            </div>
          </div>
        </div>
      ) : (
        <div className="aspect-video bg-muted flex items-center justify-center">
          {videoUrl && (
            <video src={videoUrl} controls className="w-full h-full" preload="metadata">
              Your browser does not support video.
            </video>
          )}
          {!videoUrl && <Video className="h-12 w-12 text-muted-foreground" />}
        </div>
      )}
      <CardContent className="py-2 px-3">
        <p className="text-xs text-muted-foreground">{date}</p>
        <p className="text-sm font-medium line-clamp-2">{title}</p>
        {event.content && <p className="text-xs text-muted-foreground line-clamp-2 mt-1">{event.content}</p>}
      </CardContent>
    </Card>
  );
}

function MusicCard({ event }: { event: NostrEvent }) {
  // kind 36787 Music Track NIP tags
  const audioUrl = event.tags.find(t => t[0] === 'url')?.[1];
  const fallbacks = event.tags.filter(t => t[0] === 'fallback').map(t => t[1]);
  const title = event.tags.find(t => t[0] === 'title')?.[1] || 'Untitled Track';
  const artist = event.tags.find(t => t[0] === 'artist')?.[1] || 'Hands Behind Your Back!';
  const album = event.tags.find(t => t[0] === 'album')?.[1];
  const cover = event.tags.find(t => t[0] === 'image')?.[1];
  const duration = event.tags.find(t => t[0] === 'duration')?.[1];
  const released = event.tags.find(t => t[0] === 'released')?.[1];
  const aiGenerated = event.tags.find(t => t[0] === 'ai_generated')?.[1] === 'true';
  const genres = event.tags.filter(t => t[0] === 't' && t[1] !== 'music').map(t => t[1]);
  const durationStr = duration
    ? `${Math.floor(Number(duration) / 60)}:${String(Number(duration) % 60).padStart(2, '0')}`
    : null;

  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardContent className="pt-4 pb-4">
        <div className="flex items-start gap-3">
          <div className="w-16 h-16 rounded-lg bg-muted shrink-0 overflow-hidden flex items-center justify-center">
            {cover ? (
              <img src={cover} alt={title} className="w-full h-full object-cover" />
            ) : (
              <Music className="h-6 w-6 text-muted-foreground" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <p className="font-semibold text-sm leading-tight">{title}</p>
              {durationStr && <span className="text-xs text-muted-foreground shrink-0">{durationStr}</span>}
            </div>
            <p className="text-xs text-muted-foreground mt-0.5">{artist}{album ? ` · ${album}` : ''}</p>
            <div className="flex flex-wrap items-center gap-1.5 mt-1">
              {released && <span className="text-xs text-muted-foreground">{released}</span>}
              {aiGenerated && <Badge variant="secondary" className="text-xs px-1.5 py-0">AI</Badge>}
              {genres.slice(0, 2).map(g => (
                <Badge key={g} variant="outline" className="text-xs px-1.5 py-0 capitalize">{g}</Badge>
              ))}
            </div>
            {(audioUrl || fallbacks.length > 0) && (
              <audio controls className="mt-2 w-full h-8" preload="none">
                {audioUrl && <source src={audioUrl} />}
                {fallbacks.map(fb => <source key={fb} src={fb} />)}
              </audio>
            )}
            {event.content && !audioUrl && (
              <p className="text-xs text-foreground/80 mt-1 line-clamp-2">{event.content}</p>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function MagazineCard({ event }: { event: NostrEvent }) {
  // Handles kind 34609 (Magazine), 39731 (Issue), 33953 (Ebook)
  const title = event.tags.find(t => t[0] === 'title')?.[1]
    || event.tags.find(t => t[0] === 'd')?.[1]
    || 'Publication';
  const image = event.tags.find(t => t[0] === 'image')?.[1];
  const summary = event.tags.find(t => t[0] === 'summary')?.[1] || event.content.substring(0, 120);
  const pdfUrl = event.tags.find(t => t[0] === 'url')?.[1];
  const issue = event.tags.find(t => t[0] === 'issue')?.[1];
  const authors = event.tags.filter(t => t[0] === 'author').map(t => t[1]);
  const mimeType = event.tags.find(t => t[0] === 'm')?.[1] || '';
  const isEbook = event.kind === 33953;
  const isIssue = event.kind === 39731;
  const topics = event.tags.filter(t => t[0] === 't').map(t => t[1]);
  const date = new Date(event.created_at * 1000).toLocaleDateString();

  const typeLabel = isEbook ? 'Ebook' : isIssue ? 'Issue' : 'Magazine';

  return (
    <Card className="overflow-hidden hover:shadow-md transition-shadow flex flex-col">
      {pdfUrl ? (
        <a href={pdfUrl} target="_blank" rel="noopener noreferrer" className="block">
          {image ? (
            <img src={image} alt={title} className="w-full aspect-[3/4] object-cover" loading="lazy" />
          ) : (
            <div className="aspect-[3/4] bg-muted flex items-center justify-center">
              <BookOpen className="h-10 w-10 text-muted-foreground" />
            </div>
          )}
        </a>
      ) : image ? (
        <img src={image} alt={title} className="w-full aspect-[3/4] object-cover" loading="lazy" />
      ) : (
        <div className="aspect-[3/4] bg-muted flex items-center justify-center">
          <BookOpen className="h-10 w-10 text-muted-foreground" />
        </div>
      )}
      <CardContent className="py-3 px-3 flex-1 flex flex-col">
        <div className="flex items-center gap-1.5 mb-1">
          <Badge variant="secondary" className="text-xs px-1.5 py-0">{typeLabel}</Badge>
          {mimeType.includes('epub') && <Badge variant="outline" className="text-xs px-1.5 py-0">EPUB</Badge>}
          {mimeType.includes('pdf') && <Badge variant="outline" className="text-xs px-1.5 py-0">PDF</Badge>}
        </div>
        <p className="font-semibold text-sm line-clamp-2">{title}</p>
        {issue && <p className="text-xs text-muted-foreground mt-0.5">Issue: {issue}</p>}
        {authors.length > 0 && <p className="text-xs text-muted-foreground mt-0.5">{authors.join(', ')}</p>}
        <p className="text-xs text-muted-foreground mt-0.5">{date}</p>
        {summary && <p className="text-xs text-foreground/70 mt-1 line-clamp-3">{summary}</p>}
        {topics.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-2">
            {topics.slice(0, 3).map(t => (
              <Badge key={t} variant="outline" className="text-xs px-1.5 py-0 capitalize">{t}</Badge>
            ))}
          </div>
        )}
        {pdfUrl && (
          <a href={pdfUrl} target="_blank" rel="noopener noreferrer"
             className="mt-auto pt-2 text-xs text-primary hover:underline font-medium">
            Open {isEbook ? 'Ebook' : 'Issue'} →
          </a>
        )}
      </CardContent>
    </Card>
  );
}

function EmptyState({ message }: { message: string }) {
  return (
    <Card className="border-dashed">
      <CardContent className="py-12 px-8 text-center">
        <p className="text-muted-foreground max-w-sm mx-auto">{message}</p>
      </CardContent>
    </Card>
  );
}

function LoadingGrid({ count = 4 }: { count?: number }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      {Array.from({ length: count }).map((_, i) => (
        <Card key={i}>
          <Skeleton className="h-40 w-full rounded-t-lg" />
          <CardContent className="pt-3 pb-3">
            <Skeleton className="h-4 w-3/4 mb-1" />
            <Skeleton className="h-3 w-1/2" />
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

interface NostrSectionProps {
  /** When true, shows only events WITH a content-warning tag; otherwise hides them. */
  contentWarningOnly?: boolean;
}

export function NostrSection({ contentWarningOnly = false }: NostrSectionProps) {
  const notesSafe = useAuthorNotes();
  const imagesSafe = useAuthorImages();
  const videosSafe = useAuthorVideos();
  const musicSafe = useAuthorMusic();
  const magazinesSafe = useAuthorMagazines();

  const notesCW = useAuthorNotesCW();
  const imagesCW = useAuthorImagesCW();
  const videosCW = useAuthorVideosCW();
  const musicCW = useAuthorMusicCW();
  const magazinesCW = useAuthorMagazinesCW();

  const notes = contentWarningOnly ? notesCW : notesSafe;
  const images = contentWarningOnly ? imagesCW : imagesSafe;
  const videos = contentWarningOnly ? videosCW : videosSafe;
  const music = contentWarningOnly ? musicCW : musicSafe;
  const magazines = contentWarningOnly ? magazinesCW : magazinesSafe;

  const headingLabel = contentWarningOnly ? 'For the curious' : 'On Nostr';
  const headingTitle = contentWarningOnly ? 'On Nostr' : 'Follow the Journey';
  const headingDesc = contentWarningOnly
    ? 'A selection of posts from the Nostr network — the kind that come with a warning label attached.'
    : 'Explore content published on the decentralized Nostr network by Hands Behind Your Back!';

  return (
    <section className="py-20 bg-background" id="nostr">
      <div className="max-w-5xl mx-auto px-4">
        <div className="text-center mb-12">
          <span className="inline-block px-3 py-1 rounded-full bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 text-sm font-medium mb-4">
            {headingLabel}
          </span>
          <h2 className="text-3xl md:text-4xl font-bold mb-4">{headingTitle}</h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            {headingDesc}
          </p>
        </div>

        <div className="mb-8">
          <ProfileCard />
        </div>

        <Tabs defaultValue="notes" className="w-full">
          <TabsList className="flex flex-wrap h-auto gap-1 mb-6 bg-muted/50 p-1 rounded-xl">
            <TabsTrigger value="notes" className="flex items-center gap-1.5 rounded-lg">
              <FileText className="h-3.5 w-3.5" /> Notes
            </TabsTrigger>
            <TabsTrigger value="images" className="flex items-center gap-1.5 rounded-lg">
              <ImageIcon className="h-3.5 w-3.5" /> Images
            </TabsTrigger>
            <TabsTrigger value="videos" className="flex items-center gap-1.5 rounded-lg">
              <Video className="h-3.5 w-3.5" /> Videos
            </TabsTrigger>
            <TabsTrigger value="music" className="flex items-center gap-1.5 rounded-lg">
              <Music className="h-3.5 w-3.5" /> Music
            </TabsTrigger>
            <TabsTrigger value="magazines" className="flex items-center gap-1.5 rounded-lg">
              <BookOpen className="h-3.5 w-3.5" /> Magazines
            </TabsTrigger>
          </TabsList>

          <TabsContent value="notes">
            {notes.isLoading ? (
              <div className="space-y-3">
                {Array.from({ length: 4 }).map((_, i) => (
                  <Card key={i}><CardContent className="pt-4 pb-4">
                    <div className="flex gap-3">
                      <Skeleton className="h-8 w-8 rounded-full shrink-0" />
                      <div className="flex-1"><Skeleton className="h-4 w-32 mb-2" /><Skeleton className="h-4 w-full mb-1" /><Skeleton className="h-4 w-3/4" /></div>
                    </div>
                  </CardContent></Card>
                ))}
              </div>
            ) : notes.data && notes.data.length > 0 ? (
              <div className="space-y-3">
                {notes.data.map(event => <NoteCard key={event.id} event={event} />)}
              </div>
            ) : (
              <EmptyState message="No notes found yet. Check back later!" />
            )}
          </TabsContent>

          <TabsContent value="images">
            {images.isLoading ? (
              <LoadingGrid />
            ) : images.data && images.data.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                {images.data.map(event => <ImageCard key={event.id} event={event} />)}
              </div>
            ) : (
              <EmptyState message="No images found. AI-generated images will appear here." />
            )}
          </TabsContent>

          <TabsContent value="videos">
            {videos.isLoading ? (
              <LoadingGrid />
            ) : videos.data && videos.data.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {videos.data.map(event => <VideoCard key={event.id} event={event} />)}
              </div>
            ) : (
              <EmptyState message="No videos found yet. Videos published via NIP-71 will appear here." />
            )}
          </TabsContent>

          <TabsContent value="music">
            {music.isLoading ? (
              <div className="space-y-3">
                {Array.from({ length: 3 }).map((_, i) => (
                  <Card key={i}><CardContent className="pt-4 pb-4">
                    <div className="flex gap-3">
                      <Skeleton className="h-14 w-14 rounded shrink-0" />
                      <div className="flex-1"><Skeleton className="h-4 w-40 mb-1" /><Skeleton className="h-3 w-24 mb-1" /><Skeleton className="h-3 w-16" /></div>
                    </div>
                  </CardContent></Card>
                ))}
              </div>
            ) : music.data && music.data.length > 0 ? (
              <div className="space-y-3">
                {music.data.map(event => <MusicCard key={event.id} event={event} />)}
              </div>
            ) : (
              <EmptyState message="No music tracks found yet. Music events will appear here when published." />
            )}
          </TabsContent>

          <TabsContent value="magazines">
            {magazines.isLoading ? (
              <LoadingGrid count={3} />
            ) : magazines.data && magazines.data.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                {magazines.data.map(event => <MagazineCard key={event.id} event={event} />)}
              </div>
            ) : (
              <EmptyState message="No magazines or publications found yet. PressStr publications will appear here." />
            )}
          </TabsContent>
        </Tabs>
      </div>
    </section>
  );
}
